import { Button, Dialog, Grid, MenuItem, Stack, Typography } from "@mui/material";
import useStorefrontStore from "../../../stores/public/useStorefrontStore";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import getAvailability from "../../../api/auth/items/getAvailability";
import { addDays, endOfMonth, format, isPast, startOfMonth } from "date-fns";
import Calendar from "@demark-pro/react-booking-calendar";
import PageLoading from "../../../components/layout/PageLoading";
import WushTextField from "../../../components/form/WushTextField";
import { useForm } from "react-hook-form";
import index from "../../../api/auth/items/itemBookableDurations";
import useCheckoutStore from "../../../stores/public/useCheckoutStore";
import create from "../../../api/public/storefronts/reservations/create";
import update from "../../../api/public/storefronts/reservations/update";
import toCurrency from "../../../services/toCurrency";
import useAlertStore from "../../../stores/useAlertStore";
import { ShoppingCart } from "@mui/icons-material";

export default function Item() {
  const { id } = useParams();
  const { itemById, organization } = useStorefrontStore();
  const { reservation, addReservation, updateReservation, setReservations, reservations, setOrder, order } = useCheckoutStore();
  const [ selectedDate, setSelectedDate ] = useState(null);
  const [ selectedQuantity, setSelectedQuantity ] = useState(0);
  const [ addedToCart, setAddedToCart ] = useState();

  const [ startDate, setStartDate ] = useState(startOfMonth(new Date()));
  const [ endDate, setEndDate ] = useState(endOfMonth(new Date()));
  const [ activeMonth, setActiveMonth ] = useState(new Date().getMonth());
  const [ availability, setAvailability ] = useState(null);
  const [ bookableDurations, setBookableDurations ] = useState([]);

  const showInventoryLevels = organization.storefront_display_inventory_quantities;

  const { register, handleSubmit, formState: { isDirty } } = useForm();

  const { setAlertMessage } = useAlertStore();

  const navigate = useNavigate();

  const item = itemById(id);

  useEffect(() => {
    getAvailability(id, startDate, endDate, (response) => {
      setAvailability(response);
    });
  }, [id, activeMonth]);

  useEffect(() => {
    index(id, (response) => {
      setBookableDurations(response);
    });
  }, [id]);

  const onSubmit = (data) => {
    if(reservation?.id) {
      update(organization.id, reservation.id, {
        reservation_items: [
          {
            item_id: item.id,
            quantity: data.quantity
          }
        ],
        start_at: selectedDate,
        end_at: addDays(selectedDate, 2),
        bookable_duration_id: data.bookable_duration_id
      }, (reservationResponse) => {
        updateReservation(reservationResponse);
        setOrder(reservationResponse.order);
        setAddedToCart(true);
      }, () => {
        setOrder(null);
        setReservations([]);
        setAlertMessage('Cart expired please try again', 'error');
      });
    } else {
      create(organization.id, {
        reservation_items: [
          {
            item_id: item.id,
            quantity: data.quantity
          }
        ],
        start_at: selectedDate,
        end_at: addDays(selectedDate, 2),
        bookable_duration_id: data.bookable_duration_id,
        order_id: order?.id
      }, (reservationResponse) => {
        addReservation(reservationResponse);
        setOrder(reservationResponse.order);
        setAddedToCart(true);
      }, () => {
        setOrder(null);
        setReservations([]);
        setAlertMessage('Cart expired please try again', 'error');
      });
    }
  };

  const displayInventoryAvailable = (date) => {
    const availabilityCount = availability[format(date, 'yyyy-MM-dd')];
    if(showInventoryLevels) {
      return availabilityCount > 0 ? `${availabilityCount} avail` : '0 avail';
    } else {
      return availabilityCount > 0 ? 'Available' : 'Unavailable';
    }
  };

  if(!availability) return <PageLoading />;

  return (
    <Stack>
      <Grid container spacing={4}>
        <Grid item sm={5}>
          <Stack>
            <img src={item.image_url} width="100%" alt={item.name} />
          </Stack>
        </Grid>
        <Grid item sm={7}>
          <Stack>
            <Stack>
              <Typography variant="body2" fontWeight="700">{item.item_category.name}</Typography>
            </Stack>
            <Stack mb={2}>
              <Typography variant="h3">{item.name}</Typography>
            </Stack>
            <Stack
              sx={{
                'p': {
                  fontSize: "18px"
                }
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: item.storefront_description }} />
            </Stack>
          </Stack>
          <Stack direction="row" gap={4} mb={2}>
            <Stack width="25%">
              <WushTextField
                formRegistration={register("quantity", { onChange: (e) => setSelectedQuantity(e.target.value)})}
                label="Quantity"
                defaultValue={1}
                type="number"
              />
            </Stack>
            <Stack width="75%">
              <WushTextField width="100%" formRegistration={register("bookable_duration_id")} label="Duration" select>
                {bookableDurations.map((bookableDuration) => bookableDuration.active && (
                  <MenuItem key={bookableDuration.id} value={bookableDuration.bookable_duration.id}>
                    <Typography>{bookableDuration.bookable_duration.name} - {toCurrency(bookableDuration.price_dollars)}</Typography>
                  </MenuItem>
                ))}
              </WushTextField>
            </Stack>
          </Stack>
          <Stack mb={2}>
            <Calendar
              startDate={startDate}
              endDate={endDate}
              onMonthChange={(month) => {
                const newStartDate = new Date(startDate.getFullYear(), month, 1);
                setStartDate(startDate);
                setEndDate(endOfMonth(newStartDate));
                setActiveMonth(month);
              }}
              onChange={(date) => setSelectedDate(date[0])}
              selected={[selectedDate]}
              disabled={(date) => isPast(date) || !availability[format(date, 'yyyy-MM-dd')] || availability[format(date, 'yyyy-MM-dd')] < selectedQuantity}
              variant="events"
              components={{
                DayCellFooter: (val) => !isPast(val.date) && availability[format(val.date, 'yyyy-MM-dd')] && (
                  <Typography fontSize="8px">{displayInventoryAvailable(val.date)}</Typography>
                ),
              }}
            />
          </Stack>
          <Stack>
            <Grid container direction="row" spacing={2}>
              <Grid item xs={12} sm={4}>
                <Button
                  sx={{ width: "100%" }}
                  disabled={!selectedDate || !isDirty}
                  variant="contained"
                  type="submit"
                  onClick={handleSubmit(onSubmit)}
                >
                  Add to cart
                </Button>
              </Grid>
              <Grid item xs={12} sm={4}>
                {reservations.length > 0 && <Button
                  sx={{ width: "100%" }}
                  variant="outlined"
                  onClick={() => navigate('/checkout')}
                >
                  <Stack direction="row" spacing={2}>
                    <ShoppingCart />
                    <Typography>
                      Checkout
                    </Typography>
                  </Stack>
                </Button>}
              </Grid>
            </Grid>
          </Stack>
        </Grid>
      </Grid>
      <Dialog
        open={addedToCart}
        onClose={() => setAddedToCart(false)}
      >
        <Stack p={2} textAlign="center">
          <Stack justifyContent="center">
            <Typography variant="h4">Item added to cart!</Typography>
          </Stack>
          <Stack>

          </Stack>
          <Stack direction="row" mb={2}>
            <Typography variant="h6">Would you like to continue shopping or checkout?</Typography>
          </Stack>
          <Stack direction="column" gap={2} px="10%">
            <Button
              variant="contained"
              onClick={() => {
                setAddedToCart(false);
                navigate('/checkout');
              }}
            >
              Checkout
            </Button>
            <Button
              variant="outlined"
              onClick={() => setAddedToCart(false)}
            >
              Continue Shopping
            </Button>
          </Stack>
        </Stack>
      </Dialog>
    </Stack>
  );
}